import type { CSSProperties } from 'react';
import { FontWeight, getFontFamily } from '@/theme/typography';
import { pxToRem } from '@/theme/spacing';
import { TextInputMode, type CtextinputProps } from './Ctextinput.types';
import {
  CTEXTINPUT_CHEVRON_TRANSITION,
  CTEXTINPUT_DEFAULT_FONT_SIZE_PX,
  CTEXTINPUT_FIELD_LINE_HEIGHT,
  CTEXTINPUT_LABEL_GAP_PX,
  CTEXTINPUT_MULTILINE_MARGIN,
  CTEXTINPUT_RESIZE_NONE,
  CtextinputChevronTransform,
  DEFAULT_PADDING_BLOCK_PX,
  DEFAULT_PADDING_LEFT_PX,
  DEFAULT_PADDING_RIGHT_PX,
  DROPDOWN_CHEVRON_RIGHT_OFFSET_PX,
  LTR_INPUT_STYLE,
  RTL_INPUT_STYLE,
} from './Ctextinput.constants';

export interface CtextinputStyleModel {
  wrapperStyle: CSSProperties;
  inputBaseStyle: CSSProperties;
  fieldContentStyle: CSSProperties;
  errorTextStyle: CSSProperties;
  dropdownCursorStyle?: CSSProperties;
  chevronStyle: CSSProperties;
  resolvedPadding: string;
}

interface GetCtextinputStyleModelInput {
  customWrapperStyle?: CSSProperties;
  disabled: boolean;
  fontSize?: number;
  inputContentStyle?: CSSProperties;
  inputElementStyle?: CSSProperties;
  isDropdownOpen: boolean;
  isHebrew: boolean;
  isMultiLine: boolean;
  label?: string;
  mode: TextInputMode;
  padding?: string;
  paddingRight?: number;
  textAlign?: CtextinputProps['textAlign'];
}

interface GetWrapperStyleInput {
  customWrapperStyle?: CSSProperties;
  label?: string;
}

interface GetInputBaseStyleInput {
  fontSize?: number;
  inputElementStyle?: CSSProperties;
  isHebrew: boolean;
  textAlign?: CtextinputProps['textAlign'];
}

interface GetFieldContentStyleInput {
  inputContentStyle?: CSSProperties;
  isMultiLine: boolean;
}

interface GetErrorTextStyleInput {
  mode: TextInputMode;
}

interface GetResolvedPaddingInput {
  padding?: string;
  paddingRight?: number;
}

interface GetDropdownCursorStyleInput {
  disabled: boolean;
  mode: TextInputMode;
}

interface GetChevronStyleInput {
  isDropdownOpen: boolean;
}

export function getCtextinputStyleModel({
  customWrapperStyle,
  disabled,
  fontSize,
  inputContentStyle,
  inputElementStyle,
  isDropdownOpen,
  isHebrew,
  isMultiLine,
  label,
  mode,
  padding,
  paddingRight,
  textAlign,
}: GetCtextinputStyleModelInput): CtextinputStyleModel {
  return {
    wrapperStyle: getWrapperStyle({ customWrapperStyle, label }),
    inputBaseStyle: getInputBaseStyle({ fontSize, inputElementStyle, isHebrew, textAlign }),
    fieldContentStyle: getFieldContentStyle({ inputContentStyle, isMultiLine }),
    errorTextStyle: getErrorTextStyle({ mode }),
    dropdownCursorStyle: getDropdownCursorStyle({ disabled, mode }),
    chevronStyle: getChevronStyle({ isDropdownOpen }),
    resolvedPadding: getResolvedPadding({ padding, paddingRight }),
  };
}

function getWrapperStyle({
  customWrapperStyle,
  label,
}: GetWrapperStyleInput): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: label ? pxToRem(CTEXTINPUT_LABEL_GAP_PX) : 0,
    width: '100%',
    ...customWrapperStyle,
  };
}

function getInputBaseStyle({
  fontSize,
  inputElementStyle,
  isHebrew,
  textAlign,
}: GetInputBaseStyleInput): CSSProperties {
  return {
    width: '100%',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    border: 'none',
    outline: 'none',
    fontFamily: getFontFamily(),
    fontSize: fontSize != null ? pxToRem(fontSize) : pxToRem(CTEXTINPUT_DEFAULT_FONT_SIZE_PX),
    fontWeight: FontWeight.Light,
    color: 'inherit',
    resize: CTEXTINPUT_RESIZE_NONE,
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    ...(textAlign ? { textAlign } : {}),
    ...(isHebrew ? RTL_INPUT_STYLE : LTR_INPUT_STYLE),
    ...inputElementStyle,
  };
}

function getFieldContentStyle({
  inputContentStyle,
  isMultiLine,
}: GetFieldContentStyleInput): CSSProperties {
  return {
    flex: 1,
    position: 'relative',
    lineHeight: CTEXTINPUT_FIELD_LINE_HEIGHT,
    ...(isMultiLine ? { margin: CTEXTINPUT_MULTILINE_MARGIN } : {}),
    ...inputContentStyle,
  };
}

function getErrorTextStyle({ mode }: GetErrorTextStyleInput): CSSProperties {
  return {
    position: 'absolute',
    right: mode === TextInputMode.Dropdown ? pxToRem(DROPDOWN_CHEVRON_RIGHT_OFFSET_PX) : 0,
    top: '50%',
    transform: 'translateY(-50%)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  };
}

function getResolvedPadding({
  padding,
  paddingRight,
}: GetResolvedPaddingInput): string {
  return (
    padding ??
    `${pxToRem(DEFAULT_PADDING_BLOCK_PX)} ${pxToRem(
      paddingRight ?? DEFAULT_PADDING_RIGHT_PX,
    )} ${pxToRem(DEFAULT_PADDING_BLOCK_PX)} ${pxToRem(DEFAULT_PADDING_LEFT_PX)}`
  );
}

function getDropdownCursorStyle({
  disabled,
  mode,
}: GetDropdownCursorStyleInput): CSSProperties | undefined {
  if (mode !== TextInputMode.Dropdown) {
    return undefined;
  }

  return { cursor: disabled ? 'not-allowed' : 'pointer' };
}

function getChevronStyle({ isDropdownOpen }: GetChevronStyleInput): CSSProperties {
  return {
    transform: isDropdownOpen
      ? CtextinputChevronTransform.Open
      : CtextinputChevronTransform.Closed,
    transition: CTEXTINPUT_CHEVRON_TRANSITION,
  };
}
